<script lang="ts" setup>
	import { useResourcesApi } from '~/services/apis/resources'
	import useAccountApi from '~/services/apis/account'

	const { User, StatesWithLga, BusinessSectors, CACVerified } = reactor(useUser())
	const { updateBusinessInfo } = useUser()
	const { VerifyBusinessAccountUpdate, UpdateBusinessAccount, GetAccountInfo } = useAccountApi()
	const { getStatesWithLGA, getSectorsApi } = useResourcesApi()
	const accountDetails = computed(() => ({
		name: User.value?.customer.name || '',
		sector: User.value?.customer?.meta?.sector || '',
		website: User.value?.customer?.meta?.website || '',
		email: User.value?.customer.email || '',
		phone: User.value?.phone || '',
		state: User.value?.customer?.meta?.state || '',
		city: User.value?.customer?.meta?.city || '',
		address: User.value?.customer?.meta?.address || '',
		country: 'nigeria'
	}))
	const formData = ref<Record<string, number | string | undefined>>({})
	const otpVal = shallowRef('')
	const reference = shallowRef('')
	const isUpdating = shallowRef(false)
	const isModalOpen = shallowRef(false)
	const IsOtpActive = shallowRef(false)
	const isFormSame = computed(() => {
		return JSON.stringify(accountDetails.value) === JSON.stringify(formData.value)
	})
	const city = computed(() => {
		return StatesWithLga.value.find(item => item.value === formData.value?.state)?.lga || []
	})
	const updateFormData = () => {
		formData.value = {
			name: accountDetails.value?.name || '',
			sector: accountDetails.value?.sector || '',
			email: accountDetails.value?.email || '',
			phone: accountDetails.value?.phone || '',
			state: accountDetails.value?.state || '',
			city: accountDetails.value?.city || '',
			website: accountDetails.value?.website || '',
			address: accountDetails.value?.address || '',
			country: accountDetails.value.country
		}
	}
	const verifyFormOtp = async () => {
		isUpdating.value = true
		try {
			const { data } = await VerifyBusinessAccountUpdate({
				otp: otpVal.value,
				reference: reference.value
			})
			if (data.value?.data) {
				useNotification().alert({
					status: 200,
					message: data.value.responseText
				})
				isModalOpen.value = false
				IsOtpActive.value = false
				updateBusinessInfo(data.value.data as UserAccountContext['customer'])
			}
		} finally {
			isUpdating.value = false
		}
	}

	const proceed = async () => {
		if (isFormSame.value) return null

		const result = await resolve(
			() => UpdateBusinessAccount(formData.value as UpdateBusinessAccountPayload),
			isUpdating
		)

		if (result?.data) {
			const response = result.data
			if (!(response as UpdateBusinessAccountResponse)?.otp) {
				updateBusinessInfo(response as UserAccountContext['customer'])
				await resolve(() => GetAccountInfo(), isUpdating)
				useNotification().alert({
					message: result.responseText,
					status: 200
				})
			} else {
				reference.value = (response as UpdateBusinessAccountResponse).reference
				if (!IsOtpActive.value) {
					isModalOpen.value = true
				}
			}
		}
	}
	const confirmNumberUpdate = () => {
		isModalOpen.value = false
		IsOtpActive.value = true
		useNotification().alert({
			message: 'Check your mailbox for an otp',
			status: 200
		})
	}

	onMounted(async () => {
		await resolve(() => Promise.allSettled([getSectorsApi(), getStatesWithLGA()]))
	})
	// Watch For changes and update
	watch(accountDetails, updateFormData, {
		immediate: true
	})
</script>

<template>
	<div>
		<a-form :model="formData" :colon="false" :validate-messages="antValidateMessages" @finish="proceed">
			<div>
				<a-form-item label="Business name" required name="name" class="!mb-5">
					<a-input
						v-model:value="formData.name"
						class="custom-input !bg-gray-50 !px-5"
						placeholder="John Doe Organization"
						:disabled="CACVerified"
					/>
				</a-form-item>

				<a-form-item required name="sector" label="Business Sector" class="!mb-5">
					<a-select v-model:value="formData.sector" :options="BusinessSectors" show-search />
				</a-form-item>

				<a-form-item
					label="Email Address"
					:rules="[{ required: true, type: 'email' }]"
					name="email"
					class="!mb-5"
				>
					<a-input v-model:value="formData.email" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>

				<a-form-item :rules="[{ min: 11, max: 11 }]" class="!mb-5" label="Phone Number" required name="phone">
					<a-input
						v-model:value="formData.phone"
						v-number="true"
						type="tel"
						class="custom-input !bg-gray-50 !px-5"
					/>
				</a-form-item>

				<a-form-item class="!mb-5" required name="state" label="State">
					<a-select
						v-model:value="formData.state"
						:options="StatesWithLga"
						show-search
						@change="() => (formData.city = undefined)"
					/>
				</a-form-item>

				<a-form-item class="!mb-5" required name="city" label="City">
					<a-select
						v-model:value="formData.city"
						placeholder="City of residence"
						:options="city"
						show-search
					/>
				</a-form-item>

				<a-form-item class="!mb-5" label="Address" required name="address">
					<a-input v-model:value="formData.address" class="custom-input !bg-gray-50 !px-5" />
				</a-form-item>
				<a-form-item
					label="Business Website"
					name="website"
					class="!mb-5"
					:rules="{ type: 'url', message: 'business website mube be a valid url' }"
				>
					<a-input
						v-model:value="formData.website"
						class="custom-input !bg-gray-50 !px-5"
						type="url"
						placeholder="https://"
					/>
				</a-form-item>
			</div>

			<a-button
				class="default-btn mx-auto mt-12 w-full justify-center bg-primary-2 text-white"
				html-type="submit"
				:disabled="isFormSame || isUpdating"
				:loading="isUpdating"
			>
				Update Profile
			</a-button>
		</a-form>

		<dialogs v-model:show="isModalOpen">
			<div class="flex flex-col items-center justify-center gap-y-8">
				<lazy-tools-icon dir="fill" icon="edit-2" svg-class="md:h-[130px] md:w-[131px] w-[100px] h-[100px]" />
				<shared-header
					title="Contact Details"
					sub-title="Note that your business phone number can only be changed once a year."
					class="my-2 text-center md:my-5"
				/>

				<a-button
					class="default-btn mb-10 w-full justify-center bg-primary-2 text-white"
					:disabled="isUpdating"
					@click="confirmNumberUpdate"
				>
					Continue
				</a-button>
			</div>
		</dialogs>

		<dialogs v-model:show="IsOtpActive">
			<section class="mt-10 space-y-20">
				<div class="space-y-10">
					<div class="flex flex-col items-center justify-center gap-y-2">
						<h2 class="text-center text-2xl font-medium text-[#094063]">Great! Let's Verify You</h2>
						<p class="text-center text-gray-500">
							A 4-digit code sent to <br />
							{{ formData.email }} &
							{{ formData.phone }}
						</p>
					</div>

					<div>
						<div class="flex items-center justify-center pb-6">
							<forms-otp v-model:value="otpVal" />
						</div>

						<div>
							<p class="text-center text-gray-500">
								Didn’t receive any code?
								<span
									role="button"
									aria-label="Resend otp"
									class="cursor-pointer font-medium text-primary-2 underline"
									@click="proceed"
								>
									Resend
								</span>
							</p>
						</div>
					</div>
				</div>

				<div class="w-full">
					<a-button
						class="default-btn w-full justify-center bg-primary-2 text-white"
						:disabled="isUpdating"
						:loading="isUpdating"
						@click="verifyFormOtp"
					>
						{{ isUpdating ? 'Verifying...' : 'Continue' }}
					</a-button>
				</div>
			</section>
		</dialogs>
	</div>
</template>
